import equal from 'deep-equal';
import { Trash2 } from 'lucide-react';
import CloseImage from 'images/icons/orders/close.svg';
import * as React from 'react';
import { Prompt } from 'react-router';
import styled from 'styled-components';
import { match_email } from 'validators-web';
import { getUserColor } from '../../../components_v2/avatar/Data/Data';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import DropdownColor from '../../../components_v2/dropdown/DropdownColors';
import { DropdownStyle } from '../../../components_v2/dropdown/model/Model';
import { Checkbox } from '../../../components_v2/filterList/style/Style';
import Input from '../../../components_v2/input/Input';
import InputAddress from '../../../components_v2/input/InputAddress';
import InputImage from '../../../components_v2/input/InputImage';
import { ImageResult, InputMode, InputStyle } from '../../../components_v2/input/model/Model';
import PageLoader from '../../../components_v2/pageLoader/PageLoader';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { diff } from '../../../components_v2/utils';
import { Translate, translateToNode, translateToString } from '../../../styles/global/translate';
import { AlertRes } from '../../alert/AlertProvider';
import useAlert from '../../alert/UseAlert';
import { UserAdditionalFields } from '../../authorization/action';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../../import/model';
import Restricted from '../../permissions/Restricted';
import { FlexDiv } from '../../products/style';
import { SaveButton } from '../subSettings/statusesAndTagsSettings';
import { INPUT_STYLE_SETTINGS, SystemSettingsCheckBoxWrapper, SystemSettingsDropdownWrapper, SystemSettingsWrapper, SystemSettingTitle } from '../subSettings/styles';
import { launchToastError } from '../../forms/visual-form-editor/visualFormEditor';
import { getMyProfile, putMyProfile, PutMyProfileBody } from './actions';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { detectDialCode, getDialCodeFlag, getPhoneCodeOptions } from './phoneCountryCodes';
import DoodleCreator from '../../../styles/custom-icons/doodles/doodleCreator';
import { PrimaryColor, SidelyBlack } from '../../../styles/global/css/Utils';
import { UserDoodle } from '../../../styles/custom-icons/doodles/doodle';
import { TitleAndChild } from '../../client-companies/popup/Detail';


export type UserWithAdditionalFields = UserAdditionalFields & {
	id: number,
	name: string,
	email: string,
	phone?: string,
	photo?: string,
}

type ErrorState = {
	name: boolean,
	email: boolean,
	phone: boolean,
}

const DelAddressCross = styled.div<{ size?: string, url?: string, left?: string, top?: string }>`
	position: absolute;
	width: ${p => p.size ?? '45px'};
	height: ${p => p.size ?? '45px'};
	cursor: pointer;
	bottom: 0;
	right: 0;
	${p => p.top ? `top: ${p.top};` : ''}
	${p => p.left ? `left: ${p.left};` : ''}
	background-image: url('${p => p.url ?? CloseImage}');
	background-position: center;
	background-size: 50%;
	background-repeat: no-repeat;
`;

const DelTrash = styled(DelAddressCross)`
	background-image: none;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${SidelyBlack};
	&:hover {
		color: ${PrimaryColor};
	}
`;

const PhoneInputRow = styled.div`
	display: flex;
	flex-flow: row;
	gap: 8px;
	align-items: flex-start;
`;

const PHONE_INDICATOR_DROPDOWN_STYLE: DropdownStyle = {
	containerWidth: '100px',
	width: '100%',
	optionWidth: '320px',
	optionHeight: '350px',
};

const PHONE_NUMBER_INPUT_STYLE: InputStyle = {
	...INPUT_STYLE_SETTINGS,
	containerWidth: '192px',
};

export default function MyProfile(props: {
	setToolBarState: (value: ToolbarState) => void
}) {
	const [image, setImage] = React.useState<ImageResult>();
	const [state, setState] = React.useState<UserWithAdditionalFields>();
	const [defaultState, setDefaultState] = React.useState<UserWithAdditionalFields>();
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADING);
	const [errorState, setErrorState] = React.useState<ErrorState>({ name: false, email: false, phone: false });
	const [phoneIndicator, setPhoneIndicator] = React.useState<string | undefined>('33');
	const phoneCodeOptions = React.useMemo(() => getPhoneCodeOptions(), []);
	const alert = useAlert();

	const delAddress = <DelAddressCross className='trash' onClick={e => {
		e.stopPropagation();
		if (state) setState({
			...state,
			address: undefined,
			longitude: undefined,
			latitude: undefined,
			country: undefined,
			city: undefined,
			postal_code: undefined
		});
	}} />;

	React.useEffect(() => {
		const localPhone = phoneIndicator && state?.phone?.startsWith(phoneIndicator)
			? state.phone.slice(phoneIndicator.length)
			: state?.phone ?? '';
		const phoneInvalid = localPhone.length > 0 && phoneIndicator != null
			? !isValidPhoneNumber(`+${phoneIndicator}${localPhone}`)
			: false;
		setErrorState({
			name: state?.name.trim().length === 0,
			email: state?.email !== undefined ? state.email.trim().length === 0 || !match_email(state.email.trim()) : false,
			phone: phoneInvalid,
		});
	}, [state, phoneIndicator]);

	const fetchProfile = () => getMyProfile().then(res => {
		const phoneRaw = res.phone?.startsWith('+') ? res.phone.slice(1) : res.phone;
		const normalized = { ...res, phone: phoneRaw };
		setDefaultState(JSON.parse(JSON.stringify(normalized)));
		setState(normalized);
		setImage(undefined);
		setLoadingState(LoadingStateEnum.LOADED);
		setPhoneIndicator(phoneRaw ? (detectDialCode(phoneRaw) ?? '33') : '33');
	}).catch(() => {
		setLoadingState(LoadingStateEnum.ERROR);
		alert({
			title: translateToNode('error'),
			svg: 'error',
			content: translateToNode('error_please_retry'),
			buttons: [{ title: translateToNode('retry'), res: AlertRes.Ok, style: ButtonStyle.Error }]
		}).then(res => {
			if (res.res === AlertRes.Ok) fetchProfile();
		});
	});

	React.useEffect(() => {
		fetchProfile();
	}, []);

	React.useEffect(() => {
		const disabled = (equal(diff(defaultState, state), {}) && !image) || loadingState !== LoadingStateEnum.LOADED;
		props.setToolBarState({
			bottomRightToolbarComponent: <Restricted to={{ objectAction: 'UpdateAccountSettings' }}>
				<SaveButton
					buttonStyle={ButtonStyle.White}
					disabled={disabled}
					onClick={() => {
						setState(JSON.parse(JSON.stringify(defaultState)));
						setErrorState({ name: false, email: false, phone: false });
					}}>
					{translateToNode('cancel')}
				</SaveButton>
				<SaveButton
					disabled={disabled || Object.values(errorState).some(e => e)}
					onClick={onSave}>
					{loadingState === LoadingStateEnum.LOADING
						? <FlexDiv width='100%' justify='center'><PageLoader size={22} /></FlexDiv>
						: translateToNode('save')
					}
				</SaveButton>
			</Restricted>
		});
	}, [state, image, loadingState, errorState]);

	function onSave() {
		const localPhone = phoneIndicator && state?.phone?.startsWith(phoneIndicator)
			? state.phone.slice(phoneIndicator.length)
			: state?.phone ?? '';

		if (localPhone && phoneIndicator) {
			if (!isValidPhoneNumber(`+${phoneIndicator}${localPhone}`)) {
				launchToastError(translateToString('invalid_phone_number'));
				return;
			}
		}

		setLoadingState(LoadingStateEnum.LOADING);
		const diffs = diff(defaultState, state);
		const body: PutMyProfileBody = {};
		if (diffs) {
			Object.entries(diffs).reduce((acc, [key, value]) => {
				if (value === undefined || value === null) {
					acc[key] = 'deleted';
				} else if (typeof value === 'string') {
					const trimmed = value.trim();
					if (trimmed.length === 0) {
						acc[key] = 'deleted';
					} else {
						acc[key] = { updated: trimmed };
					}
				} else {
					acc[key] = { updated: value };
				}
				return acc;
			}, body);
		}

		if (body.doodle_configuration) {
			body.doodle_configuration = { updated: state!.doodle_configuration };
		}

		// Normalize phone to E.164 format before saving
		if (body.phone && localPhone && phoneIndicator) {
			const e164 = parsePhoneNumber(`+${phoneIndicator}${localPhone}`).format('E.164').slice(1);
			body.phone = { updated: e164 };
		}

		if (image) {
			body.photo = { updated: image.content };
			body.photo_name = image.file.name;
		}
		putMyProfile(body).then(fetchProfile).catch(_ => {
			alert({
				title: translateToNode('error'),
				svg: 'error',
				content: translateToNode('error_please_retry'),
				buttons: [{ title: translateToNode('retry'), res: AlertRes.Ok, style: ButtonStyle.Error }]
			}).then(res => res.res === AlertRes.Ok && onSave());
		});
	}

	return <>
		<Prompt
			when={!equal(diff(defaultState, state), {})}
			message={translateToString('unsaved_changes_leave')}
		/>
		<SystemSettingsWrapper>
			<SystemSettingTitle>{translateToNode('my_profile')}</SystemSettingTitle>
			<SystemSettingsDropdownWrapper relative>
				{translateToNode('Picture')}
				<InputImage
					url={image?.url ?? state?.photo}
					size={60}
					compress
					disableShowImage
					onChange={setImage}
				/>
				{(state?.photo || image?.content) &&
					<DelTrash
						className='trash'
						size='30px'
						left='60px'
						top='28px'
						onClick={e => {
							e.stopPropagation();
							setImage(undefined);
							if (state) setState({
								...state,
								photo: ''
							});
						}}
					>
						<Trash2 size={15} color='currentColor' />
					</DelTrash>
				}
			</SystemSettingsDropdownWrapper>

			<SystemSettingsDropdownWrapper relative>
				{translateToNode('color')}
				<DropdownColor
					name={'color-picker'}
					selectedValue={state?.color ?? (state?.name ? getUserColor(state.name) : undefined)}
					onChange={value => {
						return state && setState({ ...state, color: value.value });
					}}
				/>
				{state?.color &&
					<DelTrash
						className='trash'
						size='30px'
						left='40px'
						top='28px'
						onClick={e => {
							e.stopPropagation();
							setImage(undefined);
							if (state) setState({
								...state,
								color: undefined
							});
						}}
					>
						<Trash2 size={15} color='currentColor' />
					</DelTrash>
				}
			</SystemSettingsDropdownWrapper>

			<SystemSettingsDropdownWrapper>
				{translateToNode('name')}
				<Input
					mode={errorState.name ? InputMode.Error : undefined}
					inputStyle={INPUT_STYLE_SETTINGS}
					name='my_name'
					type='text'
					value={state?.name}
					onChange={v => state && setState({ ...state, name: v.trim() })}
				/>
			</SystemSettingsDropdownWrapper>

			<SystemSettingsDropdownWrapper>
				{translateToNode('email')}
				<Input
					mode={errorState.email ? InputMode.Error : undefined}
					inputStyle={INPUT_STYLE_SETTINGS}
					name='my_email'
					type='text'
					value={state?.email}
					onChange={v => state && setState({ ...state, email: v.trim() })}
				/>
			</SystemSettingsDropdownWrapper>

			<SystemSettingsDropdownWrapper>
				{translateToNode('phone')}
				<PhoneInputRow>
					<Dropdown
						name='my_phone_indicator'
						dropdownStyle={PHONE_INDICATOR_DROPDOWN_STYLE}
						datalist={phoneCodeOptions}
						selectedValue={phoneIndicator ? phoneCodeOptions.find(o => o.value === phoneIndicator) : undefined}
						placeholder='+--'
						searchable
						JSXButtonInsideContainer
						JSXButton={({ value }) => (
							<span style={{ padding: '0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '14px' }}>
								{value ? `${getDialCodeFlag(value.value as string)} +${value.value}` : '+--'}
							</span>
						)}
						onChange={option => {
							if (Array.isArray(option)) return;
							const newCode = option.value;
							const localNumber = phoneIndicator && state?.phone?.startsWith(phoneIndicator)
								? state.phone.slice(phoneIndicator.length)
								: state?.phone ?? '';
							setPhoneIndicator(newCode);
							if (state) setState({ ...state, phone: newCode + localNumber });
						}}
					/>
					<Input
						inputStyle={PHONE_NUMBER_INPUT_STYLE}
						name='my_phone'
						type='tel'
						mode={errorState.phone ? InputMode.Error : undefined}
						value={
							phoneIndicator && state?.phone?.startsWith(phoneIndicator)
								? state.phone.slice(phoneIndicator.length)
								: state?.phone ?? ''
						}
						onChange={v => state && setState({ ...state, phone: (phoneIndicator ?? '') + v.trim() })}
					/>
				</PhoneInputRow>
			</SystemSettingsDropdownWrapper>

			<SystemSettingsDropdownWrapper relative>
				{translateToNode('address')}
				<InputAddress
					name=''
					type='text'
					inputStyle={INPUT_STYLE_SETTINGS}
					optionWidth={INPUT_STYLE_SETTINGS.containerWidth}
					value={state?.address}
					onChange={res => {
						if (state) setState({
							...state,
							latitude: res.latitude,
							longitude: res.longitude,
							city: res.city,
							country: res.country,
							address: res.street_number ? res.street_number + ' ' + res.route : res.route,
							postal_code: res.postcode
						});
					}}
				/>
				{state?.address && delAddress}
			</SystemSettingsDropdownWrapper>

			<SystemSettingsDropdownWrapper relative>
				{translateToNode('city')}
				<Input
					disabled
					inputStyle={INPUT_STYLE_SETTINGS}
					name='my_city'
					type='text'
					value={state?.city}
				/>
			</SystemSettingsDropdownWrapper>

			<SystemSettingsDropdownWrapper relative>
				{translateToNode('post_code')}
				<Input
					disabled
					inputStyle={INPUT_STYLE_SETTINGS}
					name='my_post_code'
					type='text'
					value={state?.postal_code}
				/>
			</SystemSettingsDropdownWrapper>

			<SystemSettingsDropdownWrapper relative>
				{translateToNode('country')}
				<Input
					disabled
					inputStyle={INPUT_STYLE_SETTINGS}
					name='my_country'
					type='text'
					value={state?.country}
				/>
			</SystemSettingsDropdownWrapper>

			<SystemSettingsCheckBoxWrapper>
				<Checkbox isActive={state?.use_address_as_default_map_center ?? false} onClick={() => state && setState({ ...state, use_address_as_default_map_center: !state.use_address_as_default_map_center })} size='20px' />
				<Translate id='use_address_as_starting_point_on_the_map' />
			</SystemSettingsCheckBoxWrapper>

			<SystemSettingsCheckBoxWrapper>
				<Checkbox isActive={state?.use_address_as_default_optimization_start ?? false} onClick={() => state && setState({ ...state, use_address_as_default_optimization_start: !state.use_address_as_default_optimization_start })} size='20px' />
				<Translate id='use_address_as_starting_point_on_the_route_optimizations' />
			</SystemSettingsCheckBoxWrapper>

			<SystemSettingsCheckBoxWrapper>
				<Checkbox isActive={state?.use_address_as_default_optimization_end ?? false} onClick={() => state && setState({ ...state, use_address_as_default_optimization_end: !state.use_address_as_default_optimization_end })} size='20px' />
				<Translate id='use_address_as_end_point_on_the_route_optimizations' />
			</SystemSettingsCheckBoxWrapper>
			<TitleAndChild
				justify='stretch'
				title={<FlexDiv gap='5px'>
					<UserDoodle size={30} /><Translate id='edit_your_doodle' /></FlexDiv>}>
				<DoodleCreator
					backgroundColor={state?.color}
					name={state?.name}
					email={state?.email}
					phone={state?.phone}
					config={state?.doodle_configuration}
					onChange={(doodle_configuration) => setState((state) => {
						if (!state) return state;
						return { ...state, doodle_configuration };
					})} />

			</TitleAndChild>
		</SystemSettingsWrapper>
	</>;
}
